import React, {memo, useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useModels23Query} from "../query";
import {getCoreRowModel, useReactTable} from "@tanstack/react-table";
import DataTable from "../../../../components/DataTable";
import StationCode from "../../../../components/columns/StationCode";
import {format, parseISO} from "date-fns";
import useDataTable from "../../../../components/DataTable/useDataTable";
import {formatFilterForAPI} from "../useFeaturesState";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import FeatureSelectionChips from "../Panels/FeatureSelectionChips";
import MDBox from "../../../../materialDashboard/components/MDBox";

export const statusIcons = {
    "OPEN": <Icon fontSize="small" color="warning">edit_document</Icon>,
    "CLOSED": <Icon fontSize="small" color="info">check</Icon>,
    "CONFIRMED": <Icon fontSize="small" color="success">lock</Icon>,
}

const cellWidth = "2rem";

const formatAspect = (aspect) => {
    if(aspect){
        return aspect.split("_").map(w => w[0]).join("")
    }else{
        return ""
    }
}

const areEqualCellValues = (prevProps, nextProps) => {
    const prevValue = prevProps.cell.getValue();
    const nextValue = nextProps.cell.getValue();
    return prevValue === nextValue;
}

const StatusCol = memo(({cell}) => {
    const status = cell.getValue();

    if (statusIcons[status]) {
        return statusIcons[status];
    }
    return null
}, areEqualCellValues)

const NoteCell = memo(({cell}) => {
    const value = cell.getValue();
    if (value !== "" && value != null) {
        return <Tooltip title={value} placement="bottom" disableInteractive>
            <span>{value.length > 16 ? value.substring(0, 15) + "..." : value}</span>
        </Tooltip>
    } else {
        return null;
    }
}, areEqualCellValues)

const SweCell = memo(({cell}) => {
    const swe = cell.row?.original?.swe;
    const sweComputed = cell.row?.original?.sweComputed;
    const sweEstimated = cell.row?.original?.sweEstimated;

    const formatSwe = (val) => {
        return isNaN(val) || val === null || val === undefined ? "" : (val?.toFixed(1) ?? '');
    }

    const value = `${formatSwe(swe)}${swe !== sweComputed ? ' *' : ''}`;
    const content = `swe: ${formatSwe(swe)}\nsweComputed: ${formatSwe(sweComputed)}\nsweEstimated: ${formatSwe(sweEstimated)}`;

    if(swe !== null && swe !== undefined){
        return <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{content}</div>} placement="bottom" disableInteractive>
            <span>{value}</span>
        </Tooltip>
    } else {
        return null;
    }
}, areEqualCellValues)


const UserCell = memo(({cell}) => {
    const user = cell.row.original.user;
    const fullName = user ? `${user?.surname} ${user.name}` : "";

    if (fullName !== "" && fullName != null) {
        return <Tooltip title={fullName} placement="bottom" disableInteractive>
            <span>{fullName.length > 8 ? fullName.substring(0, 7) + "..." : fullName}</span>
        </Tooltip>
    } else {
        return null;
    }
}, (prevProps, nextProps) => {
    return prevProps.cell.row.original.user?.id === nextProps.cell.row.original.user?.id;
});

const Models23Table = ({layers, featuresState}) => {
    const {t} = useTranslation(["webgis", "enums"]);
    const {model23SelectedState} = featuresState;
    const {selectedFeatures, handleFeatureDeSelect} = model23SelectedState;

    const {pagination, setPagination, sorting, onSortingChange, fetchDataOptions, goToFirstPage} = useDataTable();

    const params = useMemo(() => ({
        ...formatFilterForAPI.models23(layers),
        id__in: selectedFeatures.length > 0 ? selectedFeatures.map(f => f.properties.id) : undefined,
        ...fetchDataOptions
    }), [layers, fetchDataOptions, selectedFeatures]);

    const {data, isLoading} = useModels23Query(params);
    const rows = data?.rows ?? [];
    const pageCount = data?.page_count ?? -1;

    useEffect(() => {
        goToFirstPage();
    }, [layers.commonFilters, goToFirstPage]);

    const columns = useMemo(() => [
        {
            accessorKey: 'stationCode',
            cell: props => <StationCode station={props?.row?.original?.station} align="left"/>,
            header: () => <span>{t("models23Table.station")}</span>,
            enableSorting: true,
            meta: { width: "5rem", align: "left" }
        },
        {
            accessorKey: 'stationType',
            cell: props => props?.row?.original?.station?.type === "ITINERANT" && <Icon fontSize="small">hiking</Icon>,
            header: () => null,
            enableSorting: false,
            meta: { width: "3rem", align: "center" }
        },
        {
            accessorKey: 'locationName',
            cell: info => info.getValue(),
            header: () => <span>{t("models23Table.locationName")}</span>,
            enableSorting: false,
            meta: { width: "10rem", align: "left" }
        },
        {
            accessorKey: 'status',
            cell: cell => <StatusCol cell={cell}/>,
            header: () => <span>{t("models23Table.status", "Status")}</span>,
            enableSorting: false,
            meta: { width: "4rem", align: "center" }
        },
        {
            accessorKey: 'datetime',
            cell: props => format(parseISO(props.row?.original?.datetime), "dd/MM/yyyy HH:mm"),
            header: () => <span>{t("models23Table.datetime")}</span>,
            enableSorting: true,
            meta: { width: "9rem", align: "center" }
        },
        {
            accessorKey: 'elevation',
            cell: props => props.getValue() !== null ? `${props.getValue()} mt.` : "",
            header: () => <span>{t("models23Table.elevation")}</span>,
            enableSorting: false,
            meta: { width: "6rem", align: "center" }
        },
        {
            accessorKey: 'aspect',
            cell: props => formatAspect(props.getValue()),
            header: () => <span>{t("models23Table.aspect")}</span>,
            enableSorting: false,
            meta: { width: "5rem", align: "center" }
        },
        {
            accessorKey: 'slope',
            cell: props => props.getValue(),
            header: () => <span>{t("models23Table.slope")}</span>,
            enableSorting: false,
            meta: { width: "5rem", align: "center" }
        },
        ...["ww", "n", "v", "vq1", "vq2", "ta", "hs"].map(param => {
            return {
                accessorKey: param,
                cell: props => props.row?.original?.snowWeatherData[param] ?? "",
                header: () => param,
                enableSorting: false,
                meta: {
                    align: "center",
                    width: cellWidth,
                    px: "1px",
                    headerTooltip: t(`models1Table.${param}`)
                }
            }
        }),
        {
            accessorKey: "swe",
            cell: props => <SweCell cell={props} />,
            header: () => "SWE",
            enableSorting: false,
            meta: {
                align: "center",
                width: "5rem",
                px: "1px",
                headerTooltip: t('models23Table.swe', "SWE")
            }
        },
        {
            accessorKey: 'notes',
            cell: props => <NoteCell cell={props}></NoteCell>,
            header: () => <span>{t("models23Table.notes")}</span>,
            enableSorting: true,
            meta: { align: "left", px: 2 }
        },
        {
            accessorKey: 'feedback',
            cell: props => <NoteCell cell={props}></NoteCell>,
            header: () => <span>{t("models23Table.feedback", "Comment")}</span>,
            enableSorting: true,
            meta: { align: "left", width: "5rem" }
        },
        {
            accessorKey: 'user',
            cell: props => <UserCell cell={props}/>,
            header: () => <span>{t("models23Table.user", "User")}</span>,
            enableSorting: false,
            meta: { align: "center", width: "4rem" }
        },
    ], [t]);

    const table = useReactTable({
        data: rows,
        columns,
        pageCount,
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onSortingChange: onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        manualSorting: true,
    });

    const formatDateTime = (dateTime) => {
        const date = new Date(dateTime);
        return format(date, "dd/MM/yyyy HH:mm")
    };

    if (isLoading) return null;

    return (
        <MDBox>
            <FeatureSelectionChips
                selectedFeatures={selectedFeatures}
                onDeSelectFeature={handleFeatureDeSelect}
                getLabel={(feature) => `${feature.properties.station?.code || feature.properties.id} - ${formatDateTime(feature.properties.datetime)}`}
                sx={{ mb: 2, px: 2 }}
            />
            <DataTable table={table} showTotalEntries={false} />
        </MDBox>
    );
};

export default Models23Table;
